/**
 * Sidebar Component
 *
 * Main navigation sidebar for the dispatcher dashboard.
 * Migrated from prototype with AI trigger button added.
 */

import React, { useState } from 'react';
import {
  Inbox,
  LayoutDashboard,
  Map as MapIcon,
  Package,
  Book,
  Radio,
  Users,
  Truck,
  BarChart2,
  FileUp,
  Settings,
  PlusSquare,
  ChevronDown,
  PlayCircle,
  Repeat,
  Bell,
  Kanban,
  FileText,
  ChevronRight,
  Receipt,
  MapPinned,
  Calculator,
  Building,
  Cable,
  Wrench,
  MessageSquare,
  Layers,
  Globe,
  Sparkles,
  X,
} from 'lucide-react';
import { useAuth, usePermissions } from '@cfleet/auth';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  enabledAddons?: string[];
  chatUnreadCount?: number;
  inboxUnreadCount?: number;
  onOpenAIHelper?: () => void;
  onOpenImportData?: () => void;
  onOpenIntegrations?: () => void;
  onToggleAddon?: (addonId: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  setActiveTab,
  enabledAddons = [],
  chatUnreadCount,
  inboxUnreadCount = 0,
  onOpenAIHelper,
  onOpenImportData,
  onOpenIntegrations,
  onToggleAddon,
}) => {
  const { user } = useAuth();
  const { isDispatcherOrAbove, hasPermission } = usePermissions();

  // Get user display info
  const userInitials = user?.name?.substring(0, 2).toUpperCase() || 'U';
  const userName = user?.name || user?.email || 'User';

  // Collapsible state for sections
  const [planningOpen, setPlanningOpen] = useState(true);
  const [dispatchOpen, setDispatchOpen] = useState(true);
  const [clientMgmtOpen, setClientMgmtOpen] = useState(true);
  const [businessOpen, setBusinessOpen] = useState(true);

  const NavItem = ({
    id,
    icon: Icon,
    label,
    count,
    isActive,
    badge,
    isAddon,
    onRemove,
    canRemove,
  }: {
    id: string;
    icon: React.ElementType;
    label: string;
    count?: number;
    isActive?: boolean;
    badge?: string;
    isAddon?: boolean;
    onRemove?: () => void;
    canRemove?: boolean;
  }) => (
    <div
      className={`w-full flex items-center px-2 py-1.5 rounded-md transition-colors group mb-0.5 ${
        isActive ? 'bg-sidebar-hover' : 'hover:bg-sidebar-hover'
      }`}
    >
      <button
        onClick={() => setActiveTab(id)}
        className={`flex-1 flex items-center transition-colors ${
          isActive ? 'text-sidebar-active' : 'text-sidebar-text hover:text-gray-200'
        }`}
      >
        <Icon
          size={14}
          className={`mr-2.5 ${isActive ? 'text-sidebar-active' : 'text-sidebar-text group-hover:text-gray-300'}`}
        />
        <span className="flex-1 text-left font-medium text-sm">{label}</span>
        {badge && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 uppercase tracking-wide ml-1">
            {badge}
          </span>
        )}
        {count !== undefined && <span className="text-xs text-sidebar-text">{count}</span>}
      </button>
      {isAddon && canRemove && onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-rose-500/20 rounded hover:text-rose-400"
          title="Remove addon"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );

  const SectionHeader = ({
    label,
    isOpen,
    toggle,
  }: {
    label: string;
    isOpen: boolean;
    toggle: () => void;
  }) => (
    <button
      onClick={toggle}
      className="w-full flex items-center justify-between px-2 py-2 mt-4 text-sidebar-text hover:text-gray-200 transition-colors group"
    >
      <span className="text-xs font-semibold tracking-wide uppercase opacity-90">{label}</span>
      {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
    </button>
  );

  return (
    <aside className="hidden lg:flex w-[240px] bg-sidebar-bg flex-col h-full border-r border-sidebar-border text-sidebar-text shrink-0">
      {/* Workspace Header */}
      <div className="px-4 h-14 flex items-center justify-between border-b border-transparent hover:bg-sidebar-hover transition-colors cursor-pointer m-2 rounded-lg group">
        <div className="flex items-center gap-2.5">
          <div className="w-5 h-5 bg-cfleet-600 rounded flex items-center justify-center text-white text-[10px] font-bold shadow-sm">
            C
          </div>
          <span className="font-semibold text-sm text-sidebar-active">cfleet</span>
          <ChevronDown size={12} className="text-sidebar-text" />
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="p-1 hover:bg-white/10 rounded" title="New Route">
            <PlusSquare size={14} />
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="px-3 flex-1 overflow-y-auto scrollbar-hide">
        {/* Overview */}
        <div className="mb-2">
          <NavItem
            id="inbox"
            icon={Inbox}
            label="Inbox"
            count={inboxUnreadCount > 0 ? inboxUnreadCount : undefined}
            isActive={activeTab === 'inbox'}
          />
          <NavItem
            id="dashboard"
            icon={LayoutDashboard}
            label="Dashboard"
            isActive={activeTab === 'dashboard'}
          />
        </div>

        {/* Planning Section */}
        <SectionHeader
          label="Planning"
          isOpen={planningOpen}
          toggle={() => setPlanningOpen(!planningOpen)}
        />
        {planningOpen && (
          <div className="space-y-0.5">
            <NavItem
              id="routes"
              icon={MapIcon}
              label="Routes Map"
              isActive={activeTab === 'routes'}
            />
            {isDispatcherOrAbove && (
              <NavItem
                id="address_book"
                icon={Book}
                label="Address Book"
                isActive={activeTab === 'address_book'}
              />
            )}
            <NavItem
              id="simulation"
              icon={PlayCircle}
              label="Route Simulation"
              isActive={activeTab === 'simulation'}
              badge="Preview"
            />
            <NavItem
              id="recurring"
              icon={Repeat}
              label="Recurring"
              isActive={activeTab === 'recurring'}
            />
          </div>
        )}

        {/* Dispatch & Fleet Section */}
        <SectionHeader
          label="Dispatch & Fleet"
          isOpen={dispatchOpen}
          toggle={() => setDispatchOpen(!dispatchOpen)}
        />
        {dispatchOpen && (
          <div className="space-y-0.5">
            <NavItem
              id="dispatch_board"
              icon={Kanban}
              label="Dispatch Board"
              isActive={activeTab === 'dispatch_board'}
            />
            <NavItem
              id="tracking"
              icon={Radio}
              label="Live Tracking"
              isActive={activeTab === 'tracking'}
            />
            <NavItem
              id="drivers"
              icon={Users}
              label="Drivers & Team"
              count={8}
              isActive={activeTab === 'drivers'}
            />
            <NavItem
              id="vehicles"
              icon={Truck}
              label="Vehicles"
              isActive={activeTab === 'vehicles'}
            />
            <NavItem
              id="regions"
              icon={Layers}
              label="Regions"
              isActive={activeTab === 'regions'}
            />
            <NavItem
              id="alerts"
              icon={Bell}
              label="Real-time Alerts"
              isActive={activeTab === 'alerts'}
            />

            {/* Dynamic Addon: Maintenance */}
            {enabledAddons.includes('addon_maintenance') && (
              <NavItem
                id="maintenance"
                icon={Wrench}
                label="Maintenance"
                isActive={activeTab === 'maintenance'}
                isAddon
                canRemove={user?.role === 'Admin'}
                onRemove={() => onToggleAddon?.('addon_maintenance')}
              />
            )}
            {/* Dynamic Addon: Driver Chat */}
            {enabledAddons.includes('addon_chat') && (
              <NavItem
                id="chat"
                icon={MessageSquare}
                label="Team Chat"
                isActive={activeTab === 'chat'}
                count={chatUnreadCount}
                isAddon
                canRemove={user?.role === 'Admin'}
                onRemove={() => onToggleAddon?.('addon_chat')}
              />
            )}
          </div>
        )}

        {/* Client Management Section */}
        <SectionHeader
          label="Client Management"
          isOpen={clientMgmtOpen}
          toggle={() => setClientMgmtOpen(!clientMgmtOpen)}
        />
        {clientMgmtOpen && (
          <div className="space-y-0.5">
            <NavItem
              id="invoices"
              icon={Receipt}
              label="Invoices"
              isActive={activeTab === 'invoices'}
            />
            <NavItem
              id="service_areas"
              icon={MapPinned}
              label="Service Areas"
              isActive={activeTab === 'service_areas'}
            />
            <NavItem
              id="rate_tables"
              icon={Calculator}
              label="Rate Tables"
              isActive={activeTab === 'rate_tables'}
            />
            <NavItem
              id="clients"
              icon={Building}
              label="Clients"
              isActive={activeTab === 'clients'}
            />
            {/* Dynamic Addon: Customer Portal */}
            {enabledAddons.includes('addon_portal') && (
              <NavItem
                id="portal"
                icon={Globe}
                label="Portal Config"
                isActive={activeTab === 'portal'}
                isAddon
                canRemove={user?.role === 'Admin'}
                onRemove={() => onToggleAddon?.('addon_portal')}
              />
            )}
          </div>
        )}

        {/* Business Section */}
        <SectionHeader
          label="Business"
          isOpen={businessOpen}
          toggle={() => setBusinessOpen(!businessOpen)}
        />
        {businessOpen && (
          <div className="space-y-0.5">
            <NavItem id="orders" icon={Package} label="Orders" isActive={activeTab === 'orders'} />
            <NavItem
              id="analytics"
              icon={BarChart2}
              label="Analytics"
              isActive={activeTab === 'analytics'}
            />
            {hasPermission('viewAudit') && (
              <NavItem
                id="audit"
                icon={FileText}
                label="Audit Logs"
                isActive={activeTab === 'audit'}
              />
            )}
          </div>
        )}
      </div>

      {/* Bottom Section */}
      <div className="p-3 border-t border-sidebar-border mt-auto">
        <div className="mb-2">
          <button
            onClick={onOpenImportData}
            className="w-full flex items-center px-2 py-1.5 rounded-md text-sidebar-text hover:bg-sidebar-hover hover:text-gray-200 transition-colors"
          >
            <FileUp size={14} className="mr-2.5" />
            <span className="text-sm font-medium">Import Data</span>
          </button>
          <button
            onClick={onOpenIntegrations}
            className="w-full flex items-center px-2 py-1.5 rounded-md text-sidebar-text hover:bg-sidebar-hover hover:text-gray-200 transition-colors"
          >
            <Cable size={14} className="mr-2.5" />
            <span className="text-sm font-medium">Integrations</span>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center px-2 py-1.5 rounded-md transition-colors
                    ${
                      activeTab === 'settings'
                        ? 'bg-sidebar-hover text-sidebar-active'
                        : 'text-sidebar-text hover:bg-sidebar-hover hover:text-gray-200'
                    }
                `}
          >
            <Settings
              size={14}
              className={`mr-2.5 ${activeTab === 'settings' ? 'text-sidebar-active' : ''}`}
            />
            <span className="text-sm font-medium">Settings</span>
          </button>
        </div>

        {/* AI Trigger Button with User Info */}
        <div
          onClick={onOpenAIHelper}
          className="pt-2 flex items-center justify-between px-2 cursor-pointer hover:text-gray-200 transition-colors group bg-transparent hover:bg-sidebar-hover rounded-md py-1.5"
          title="AI Fleet Assistant"
        >
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-[9px] font-bold text-white shadow-sm ring-1 ring-white/10">
              {userInitials}
            </div>
            <span className="text-xs font-medium">{userName}</span>
          </div>
          <div className="relative">
            <Sparkles
              size={14}
              className="opacity-60 group-hover:opacity-100 transition-opacity text-indigo-400"
            />
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-cfleet-500 rounded-full border border-sidebar-bg animate-pulse"></div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
